import { useEffect } from "react";

import Tracker from "@readai/common/tracking/Tracker";

const createMetaEventTitle = (title: string): HTMLMetaElement => {
  const el = document.createElement("meta");
  el.setAttribute("name", "mptitle");
  el.content = title;
  return el;
};

const getMetaTitle = () =>
  document.querySelector("meta[name=mptitle]") as HTMLMetaElement | null;

export interface UsePageTitleOptions {
  /* title to send to analytics (mixpanel) */
  eventTitle?: string;
  onPageTitleChange?: (title: string) => void;
}

/**
 * Hook to set the page title and send a page view event to analytics
 *
 * @example
 * usePageTitle(t`Welcome to this Page`, { eventTitle: "Welcome to this Page" });
 *
 * @param title - title to set, should be the translated string for the user's language
 * @param options - UsePageTitleOptions
 */
const usePageTitle = (title: string, options: UsePageTitleOptions = {}) => {
  useEffect(() => {
    const documentTitle = `Read - ${title}`;
    document.title = documentTitle;

    options.onPageTitleChange?.(title);

    const existingMetaTag = getMetaTitle();
    if (existingMetaTag) {
      existingMetaTag.content = documentTitle;
    } else {
      (document.head ?? document.querySelector("head")).appendChild(
        createMetaEventTitle(documentTitle),
      );
    }
  }, [title]);

  useEffect(() => {
    Tracker.pageviewEvent({
      page_title: options?.eventTitle ?? title,
    });
  }, [title]);
};

export default usePageTitle;
